/**
 * Bynder asset picker — React port of the legacy in-tool browser
 * (window.__bynderPick, 2026-07-25 final version):
 * - opens as a modal; when a division is set, the browse view shows that
 *   division's asset CATEGORIES (Exteriors, Interiors, EI, Streetscapes,
 *   Amenities, Aerials, Events, 5-Star, Active Adult, Next Gen, Solar) with a
 *   live thumbnail preview per tile, plus the Stock categories
 * - clicking a category opens a paginated result grid (Load more, 30/page)
 * - the search box keyword-searches, division-scoped when a division is set
 * - clicking an asset returns its hosted URL via onPick and closes
 */
import { useCallback, useEffect, useState } from "react";
import { bynderSearch, type BynderAsset } from "../services/api";
import { BYN_CATS, BYN_STOCK, filtersFor, type BynderCategory } from "../services/bynder";
import { isGifAsset } from "../services/bynderGif";
import { bynderBase } from "../services/bynderCrop";
import { divComms } from "../services/snapshots";

/** MULTI-SELECT (2026-09-16, for the hero GIF builder — the monolith's `__bynderPick({multi:true})`):
    tiles toggle instead of picking, each selected one wears its order number, and a Done bar
    returns the set. `initial` pre-selects what the builder already holds; `max` caps the set. */
export interface BynderMulti { initial: string[]; max: number; title?: string; hint?: string; onDone: (urls: string[], assets: BynderAsset[]) => void }

interface Props {
  divisionCode?: string;
  onPick: (url: string, asset: BynderAsset) => void;
  /** the SLOT's declared pixels, so the transform asks Bynder for exactly that */
  needW?: number;
  needH?: number;
  onClose: () => void;
  multi?: BynderMulti;
}

type View = { mode: "browse" } | { mode: "results"; title: string; q?: string; filters?: string; cat?: BynderCategory; comm?: string };

function CategoryTile({ cat, div, commQ, onOpen }: { cat: BynderCategory; div?: string; commQ?: string; onOpen: () => void }) {
  const [thumb, setThumb] = useState<string>("");
  useEffect(() => {
    let live = true;
    const q = cat.div ? commQ || undefined : undefined;
    bynderSearch({ filters: filtersFor(cat, div), q, limit: 1 })
      /* a community with nothing tagged shows the division's tile, not a blank (#130) */
      .then((j) => (j.ok && j.items[0]) || !q ? j : bynderSearch({ filters: filtersFor(cat, div), limit: 1 }))
      .then((j) => { if (live && j.ok && j.items[0]) setThumb(j.items[0].thumb || j.items[0].url); })
      .catch(() => {});
    return () => { live = false; };
  }, [cat, div, commQ]);
  return (
    <button type="button" className="byn-tile" onClick={onOpen}>
      <span className={"byn-timg" + (thumb ? "" : " ph")} style={thumb ? { backgroundImage: `url('${thumb}')` } : undefined} />
      <span className="byn-tlbl">{cat.label}</span>
    </button>
  );
}

/**
 * IS THIS ASSET USABLE IN AN EMAIL?
 *
 * ONLY `url` — the web-size HOSTED address — can go in a send. `thumb` is Bynder's
 * own preview (`lennar.bynder.com/asset/<id>/thumbnail/webimage/...`): fine for this
 * grid, useless in an email, because a recipient's client loads it from
 * bynder.com rather than image.news.lennar.com and gets nothing.
 *
 * THE BUG THIS FIXES (2026-08-21, Jack: "why is the main hero image not populating
 * after i select an image?"). The tile used to fall back — `it.url || it.thumb` —
 * so an asset with no web-size derivative silently stored its THUMBNAIL. Measured
 * on his build: the Inspector card showed
 * `lennar.bynder.com/asset/5608fde5-…/thumbnail/webimage…` while the canvas still
 * painted the block's shipped placeholder, and the Done gate counted the slot as
 * FILLED — so the email would have gone out with no hero at all and nothing
 * flagged. A refusal the requester can see beats a silent bad pick.
 */
function pickable(it: { url?: string }): boolean {
  return /^https?:\/\//i.test(String(it.url || "").trim());
}

/**
 * THE URL FOR A SLOT OF A DECLARED SIZE (2026-08-22).
 *
 * Every ECH image field declares its pixels — Main Image is 600×350, the plan cards
 * 270×180 — and Bynder can return exactly that from the transform base, so ask for
 * it rather than scaling a one-size-fits-all file. Verified live on a 7952×5304
 * original: `?io=transform:fill,width:600,height:350` came back 600×350.
 *
 * `fill` (not `fit`) because the slot's shape is fixed and locked in the block's
 * markup: fitting would letterbox, filling crops to the frame the design expects.
 * With no transform base, or no declared size, the asset's own `url` stands.
 */
export function transformBase(it: { transform?: string; thumbnails?: Record<string, string> }): string {
  /* ── READ THE NESTED FIELD TOO (2026-08-26) ────────────────────────────────────
     The BE port of /bynder-search built its item from `m.thumbnails` but never
     surfaced `transformBaseUrl` as `transform`, so every asset arrived without one
     and the fallback below stored `thumbnails.webimage` — a `/asset/<id>/thumbnail/`
     PREVIEW. The canvas rendered it (a browser can load one) and the sent email
     would not (no email client can), which is what the required-gate was correctly
     flagging as a missing photo while the requester could see it on screen.
     Measured live: every ATL Exteriors asset, `transform` absent,
     `thumbnails.transformBaseUrl` present and correct.
     The BE is fixed too; reading both means whichever side deploys first works, and
     an older cached bundle keeps working after the BE change. */
  const t = it as { transform?: string; thumbnails?: Record<string, string> };
  const th = t.thumbnails || {};
  return String(t.transform || th.transformBaseUrl || th.transformbaseurl || "").trim();
}

export function urlForSlot(it: { url?: string; transform?: string; thumbnails?: Record<string, string>; ext?: string; name?: string }, w?: number, h?: number): string {
  const base = transformBase(it);
  if (!base) return String(it.url || "");
  /* ── A GIF IS NEVER TRANSFORMED (2026-09-16) ─────────────────────────────────────
     Bynder's `io=transform:…` renders a single frame, so the slot-size fill that every
     photo gets would ship a still. A GIF pick stores the bare base — the file as
     published — and the card, not this URL, explains the size (services/bynderGif.ts). */
  if (isGifAsset(it)) return base;
  /* ── NO DECLARED SIZE IS NOT A REASON TO STORE THE PREVIEW (2026-09-09) ──────────
     Katy, on a Featured Plan (2 Buttons) build: "When i try to change the image it's
     giving an error on the new images" — three "Plan image · 270×180" blanks on the
     Done gate. The plan card's photo button opened this picker WITHOUT the slot size,
     so this function fell through to `it.url`, which is the `/asset/<id>/thumbnail/`
     preview — the exact URL `imageBlank` exists to flag, because no email client can
     load it. The canvas showed her new photo; the gate called it missing; both were
     right about what they were looking at.
     The transform base alone is a real, email-safe image (Bynder serves the asset
     from it with no `io=` parameters), so with a base and no size that is what a pick
     stores. The preview URL is now only ever stored when there is no base at all. */
  if (!w || !h) return base;
  return base + "?io=transform:fill,width:" + Math.round(w) + ",height:" + Math.round(h);
}

export default function BynderPicker({ divisionCode, onPick, onClose, needW, needH, multi }: Props) {
  const [warn, setWarn] = useState("");
  /* the multi-select set, in pick order; keyed by the URL the slot would store */
  const [sel, setSel] = useState<{ url: string; asset: BynderAsset }[]>(() => (multi ? multi.initial.map((u) => ({ url: u, asset: { id: u, name: "", thumb: u, url: u } })) : []));
  /* matched on the transform BASE: a frame the builder already cropped (`io=transform:crop…`)
     is still the same photo, so its tile reads selected and toggling it off removes THAT entry */
  const same = (a: string, b: string) => (bynderBase(a) || a) === (bynderBase(b) || b);
  const toggle = (it: BynderAsset) => {
    const url = urlForSlot(it, needW, needH);
    setSel((cur) => {
      const i = cur.findIndex((x) => same(x.url, url));
      if (i > -1) return cur.filter((_x, j) => j !== i);
      if (multi && cur.length >= multi.max) return cur;
      return [...cur, { url, asset: it }];
    });
  };
  const selIdx = (it: BynderAsset) => { const url = urlForSlot(it, needW, needH); return sel.findIndex((x) => same(x.url, url)); };
  const dv = (divisionCode || "").trim().toUpperCase();
  const [view, setView] = useState<View>({ mode: "browse" });
  const [q, setQ] = useState("");
  /* ── COMMUNITY FILTER (2026-08-21, Jack: "for bynder need to have a community
     filter") — the division's roster, "" = all. Scoping is a KEYWORD, not a
     metaproperty: Bynder filenames concatenate the community name, so the term is
     stripped of non-alphanumerics for category scoping (the Ad Creative Studio's
     picker proved a spaced name returns 0, 2026-08-04). Opens on "All communities"
     — a preseed would silently scope every search (verified there 2026-07-30). */
  const [comm, setComm] = useState("");
  const comms = dv ? divComms(dv) : [];
  const commQOf = (c: string) => c.replace(/[^A-Za-z0-9]/g, "");
  const [items, setItems] = useState<BynderAsset[]>([]);
  const [page, setPage] = useState(1);
  const [more, setMore] = useState(false);
  const [loading, setLoading] = useState(false);
  /* ── NOTHING TAGGED FOR THIS COMMUNITY → THE DIVISION'S PHOTOS, SAID OUT LOUD (2026-09-22,
     pilot #130, Kailey White NEV: "the filter for the community doesn't bring up anything").
     The community scope is a filename keyword, and a newer community's photos are often
     not named for it yet, so the grid came back "No assets here yet" and read as broken.
     A category opened under a community that returns nothing on page 1 re-runs without
     the keyword — the same division-scoped results the "All communities" view shows —
     and one line says so, in the words Jack approved. `fell` is the community it fell
     back from; "" means the scope held. */
  const [fell, setFell] = useState("");

  const load = useCallback((v: View, p: number, append: boolean) => {
    if (v.mode !== "results") return;
    setLoading(true);
    if (!append) setFell("");
    bynderSearch({ q: v.q, filters: v.filters, limit: 30, page: p })
      .then((j) => {
        const got = j.ok ? j.items : [];
        if (!got.length && !append && v.cat && v.comm && v.q) {
          return bynderSearch({ filters: v.filters, limit: 30, page: 1 }).then((j2) => {
            const all = j2.ok ? j2.items : [];
            if (all.length) {
              setFell(v.comm as string);
              /* "Load more" pages the DIVISION's results from here on, not the empty scope */
              setView({ ...v, q: undefined });
            }
            return all;
          });
        }
        return got;
      })
      .then((got) => {
        setItems((s) => (append ? [...s, ...got] : got));
        setMore(got.length >= 30);
        setPage(p);
      })
      .catch(() => { if (!append) setItems([]); setMore(false); })
      .finally(() => setLoading(false));
  }, []);

  function openResults(title: string, opts: { q?: string; filters?: string; cat?: BynderCategory; comm?: string }) {
    const v: View = { mode: "results", title, ...opts };
    setView(v);
    setItems([]);
    load(v, 1, false);
  }
  function openCat(c: BynderCategory, community: string) {
    openResults(c.label + (c.div && community ? " · " + community : ""), {
      filters: filtersFor(c, dv || undefined),
      q: c.div && community ? commQOf(community) : undefined,
      cat: c,
      comm: c.div && community ? community : undefined,
    });
  }
  function search(community = comm) {
    const kw = q.trim();
    /* the community rides along as a search term, raw (the ACS picker's own move) */
    const terms = [kw, community].filter(Boolean).join(" ");
    if (!terms) { setView({ mode: "browse" }); return; }
    openResults(`“${terms}”` + (dv ? ` in ${dv}` : ""), { q: terms, filters: dv ? "Division_Code:" + dv : undefined });
  }
  function onComm(c: string) {
    setComm(c);
    /* a change mid-results re-runs what's on screen under the new scope */
    if (view.mode === "results") {
      if (view.cat) openCat(view.cat, c);
      else search(c);
    }
  }

  useEffect(() => {
    const onEsc = (e: KeyboardEvent) => { if (e.key === "Escape") onClose(); };
    document.addEventListener("keydown", onEsc);
    return () => document.removeEventListener("keydown", onEsc);
  }, [onClose]);

  return (
    <div className="byn-ovl" onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}>
      <div className="byn-modal">
        <div className="byn-hd">
          <span className="byn-ttl">📁 {multi && multi.title ? multi.title : "Bynder Assets"}{dv && <span className="byn-div">{dv}</span>}</span>
          <span className="byn-sr">
            <input
              className="byn-q"
              placeholder="Search all Bynder…"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); search(); } }}
            />
            <button type="button" className="byn-x" onClick={onClose} title="Close">×</button>
          </span>
        </div>

        <div className="byn-bd">
          {comms.length > 0 && (
            <div className="byn-commw">
              <select className="byn-comm" data-byncomm value={comm} onChange={(e) => onComm(e.target.value)}>
                <option value="">All communities</option>
                {comms.map((c) => <option key={c} value={c}>{c}</option>)}
              </select>
            </div>
          )}
          {view.mode === "browse" && (
            <>
              {!dv && <div className="byn-note">No division picked in Step 1 — showing the full library. Pick a division to scope these.</div>}
              <div className="byn-sec">{dv ? dv + " Assets" : "Division Assets"}{comm && <span className="byn-seccomm"> · {comm}</span>}</div>
              <div className="byn-grid">
                {BYN_CATS.map((c) => (
                  <CategoryTile key={c.label} cat={c} div={dv || undefined} commQ={commQOf(comm)} onOpen={() => openCat(c, comm)} />
                ))}
              </div>
              <div className="byn-sec" style={{ marginTop: 28 }}>Stock</div>
              <div className="byn-grid">
                {BYN_STOCK.map((c) => (
                  <CategoryTile key={c.label} cat={c} onOpen={() => openCat(c, comm)} />
                ))}
              </div>
            </>
          )}

          {view.mode === "results" && (
            <>
              <button type="button" className="byn-back" onClick={() => { setView({ mode: "browse" }); setItems([]); }}>
                ← All categories
              </button>
              <div className="byn-sec" style={{ fontSize: 20, margin: "2px 0 16px" }}>{view.title}</div>
              {fell && view.cat && (
                <div className="byn-note" data-testid="byn-fellback">
                  No photos tagged {fell} yet — showing all {dv ? dv + " " : ""}{view.cat.label}.
                </div>
              )}
              <div className="byn-rgrid">
                {loading && !items.length && <div className="byn-msg">Loading…</div>}
                {!loading && !items.length && <div className="byn-msg">No assets here yet. Try another category or the search box.</div>}
                {warn && (
                  <div className="byn-msg byn-warn">
                    “{warn}” has no web-size asset in Bynder, so there is nothing an email can load. Pick another, or ask for it to be published.
                  </div>
                )}
                {items.map((it, i) => (
                  <button key={it.id + i} type="button" className={"byn-a" + (pickable(it) ? "" : " byn-no") + (multi && selIdx(it) > -1 ? " on" : "")}
                    title={pickable(it) ? it.name : it.name + " — no web-size asset, cannot be used in an email"}
                    onClick={() => { if (!pickable(it)) { setWarn(it.name || "That asset"); return; } if (multi) { toggle(it); return; } onPick(urlForSlot(it, needW, needH), it); onClose(); }}>
                    <span className="byn-ath" style={{ backgroundImage: `url('${it.thumb || it.url}')` }}>
                      {isGifAsset(it) && <span className="byn-gif" title="Animated GIF — placed as-is, no crop; Outlook on Windows shows the first frame">GIF</span>}
                      {multi && selIdx(it) > -1 && <span className="byn-sel">{selIdx(it) + 1}</span>}
                    </span>
                    <span className="byn-anm">{it.name || "asset"}</span>
                  </button>
                ))}
                {more && (
                  <button type="button" className="byn-more" disabled={loading} onClick={() => load(view, page + 1, true)}>
                    {loading ? "Loading…" : "Load more"}
                  </button>
                )}
              </div>
            </>
          )}
        </div>
        {multi && (
          <div className="byn-multibar" data-testid="byn-multibar">
            <span className="byn-multin">{sel.length} of {multi.max} selected{multi.hint ? " · " + multi.hint : " — tap photos across categories, then Done"}</span>
            <button type="button" className="byn-multidone" disabled={!sel.length} onClick={() => { multi.onDone(sel.map((x) => x.url), sel.map((x) => x.asset)); onClose(); }}>Done</button>
          </div>
        )}
      </div>
    </div>
  );
}
